/**
 * Image models available to AI Studio.
 *
 * `GET /api/studio-ai/settings` lists the models an admin has enabled, the
 * Studio default, and whether users may choose at all. Cached for a minute —
 * the list changes only when an admin edits the registry, and the backend's own
 * settings cache has a 300 s TTL anyway.
 *
 * A backend without the model registry answers without `image.models`; that
 * degrades to an empty list, which hides the picker and leaves every quality
 * option at the pre-2.5 set.
 */

import { useCallback, useMemo } from "react"

import { useQuery } from "@tanstack/react-query"

import type { StudioImageModel } from "@/features/studio-ai/types/studio-ai.types"
import type { ImageModelCapabilities } from "@/features/studio-ai/utils/image-model-capabilities"
import type { ImageSettings } from "@/stores/studio-ai-store"

import { getStudioSettings } from "@/api/studio-ai-api"
import {
  getImageModelCapabilities,
  resolveSelectedImageModel,
  selectableModels,
} from "@/features/studio-ai/utils/image-model-capabilities"
import { useStudioAIStore } from "@/stores/studio-ai-store"

/** Local key: `lib/query-keys.ts` is owned elsewhere in this change. */
const STUDIO_IMAGE_MODELS_QUERY_KEY = ["studio-ai", "image-models"] as const

export interface StudioImageModelsResult {
  /** Models the user may pick from (disabled entries removed). */
  models: StudioImageModel[]
  /** Id the backend uses when the request carries no `model_id`. */
  defaultModelId?: string
  /** Whether the admin lets Studio users choose a model at all. */
  userSelectable: boolean
  isLoading: boolean
}

export function useStudioImageModels(): StudioImageModelsResult {
  const { data, isLoading } = useQuery({
    queryKey: STUDIO_IMAGE_MODELS_QUERY_KEY,
    queryFn: getStudioSettings,
    staleTime: 60_000,
  })

  const image = data?.image
  const models = useMemo(() => selectableModels(image?.models), [image?.models])

  return {
    models,
    defaultModelId: image?.default_model_id,
    userSelectable: image?.user_selectable === true,
    isLoading,
  }
}

export interface SelectedImageModelResult extends StudioImageModelsResult {
  /** The model this surface will generate with, or undefined while loading. */
  selectedModel?: StudioImageModel
  /** Quality options / transparent support for `selectedModel`. */
  capabilities: ImageModelCapabilities
  /** Value to send as `model_id`; undefined when the backend should decide. */
  requestModelId?: string
  /** True when a picker should render: admin allows it and there is a choice. */
  showModelPicker: boolean
  /**
   * Switch model and repair any setting the new model can't honour, in a single
   * store write (each write persists the whole image-settings section).
   */
  selectModel: (modelId: string) => void
}

/**
 * `useStudioImageModels` plus the current selection from the store.
 *
 * The store may hold a `modelId` an admin has since removed; resolution happens
 * here rather than in the store so the fallback always reflects the list the
 * user is looking at right now. When the admin has turned selection off, the
 * stored id is ignored so capabilities and `model_id` follow the Studio default.
 */
export function useSelectedImageModel(): SelectedImageModelResult {
  const base = useStudioImageModels()
  const imageSettings = useStudioAIStore((state) => state.imageSettings)
  const updateImageSettings = useStudioAIStore((state) => state.updateImageSettings)

  const selectedModel = useMemo(
    () =>
      resolveSelectedImageModel(
        base.models,
        base.userSelectable ? imageSettings.modelId : undefined,
        base.defaultModelId
      ),
    [base.models, base.userSelectable, imageSettings.modelId, base.defaultModelId]
  )
  const capabilities = useMemo(() => getImageModelCapabilities(selectedModel), [selectedModel])

  const currentQuality = imageSettings.quality
  const selectModel = useCallback(
    (modelId: string) => {
      const next = base.models.find((model) => model.id === modelId)
      const nextCapabilities = getImageModelCapabilities(next)
      const patch: Partial<ImageSettings> = { modelId }
      if (!nextCapabilities.qualities.includes(currentQuality)) {
        patch.quality = nextCapabilities.defaultQuality
      }
      if (!nextCapabilities.supportsTransparent) {
        patch.background = "opaque"
      }
      updateImageSettings(patch)
    },
    [base.models, currentQuality, updateImageSettings]
  )

  return {
    ...base,
    selectedModel,
    capabilities,
    requestModelId: selectedModel?.id ?? base.defaultModelId,
    showModelPicker: base.userSelectable && base.models.length > 1,
    selectModel,
  }
}
